import * as React from "react"
import { Hidden, Row, Visible } from "react-grid-system"
import styled from "styled-components"
import { Spacer } from "../spacers"
import { theme } from "../../theme"
import Checkbox from "./Checkbox"
import H3 from "../typography/H3"
import { MediaQuery } from "../../../utils/design-helpers"
import BodyLarge from "../typography/BodyLarge"
import Modal from "../../molecules/Modals/Modal"
import { AnimatePresence } from "framer-motion"
import { useClickOutside } from "../../../hooks/useClickOutside"
import { ArrowDropUpIcon } from "../icons/ArrowDropUpIcon"
import { ArrowDropDownIcon } from "../icons/ArrowDropDownIcon"

interface DropdownSelectProps {
  items: Array<{ id: string | number; value?: string; title: string }>
  onClickItem: (item: any) => void
  selectedValues?: Array<any>
  title: string
  selectAllTitle: string
  onClickSelectAll: () => void
}

const ButtonContainer = styled.div<{ open: boolean }>`
  cursor: pointer;
  width: 100%;
  border-bottom: 3px solid #616161;
  padding-bottom: 6px;
`

const DropdownContentContainer = styled.div<{ open: boolean }>`
  background-color: white;
  filter: drop-shadow(0px 0px 4px rgba(119, 119, 119, 0.25));
  display: ${(props) => (props.open ? "block" : "none")};
  border-radius: ${(props) => props.theme.borders.softRadius};
  position: absolute;
  padding: 1.5rem;
  min-width: 130%;
  z-index: 3;
  max-height: 60vh;
  overflow-y: scroll;

  ${MediaQuery.XS} {
    width: 90%;
    min-width: 90%;
  }
`

const CheckingRow = styled.div`
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  border-radius: ${(props) => props.theme.borders.buttonRadius};
  align-items: center;
  display: flex;
  margin: 0.5rem;

  :hover {
    background-color: ${(props) => props.theme.colors.greyLight};
  }
`

const ItemsContainer = styled.div`
  padding-left: ${(props) => props.theme.spacings.large};
`

const FilterDropdown: React.FunctionComponent<DropdownSelectProps> = ({
  items,
  onClickItem,
  selectedValues,
  title,
  selectAllTitle,
  onClickSelectAll,
}) => {
  const [open, setOpen] = React.useState(false)
  const isChecked = (id: string | number) => {
    return selectedValues?.includes(id)
  }

  const dropdownRef = useClickOutside<HTMLDivElement>(() => setOpen(false))

  const DropdownContent = () => (
    <>
      <CheckingRow onClick={onClickSelectAll}>
        <Checkbox checked={selectedValues?.length === items.length} />
        <Spacer spacing={0.5} />
        <H3>{selectAllTitle}</H3>
      </CheckingRow>
      <ItemsContainer>
        {items.map((item) => (
          <CheckingRow key={item.id} onClick={() => onClickItem(item.id)}>
            <Checkbox checked={isChecked(item.id)} />
            <Spacer spacing={0.5} />
            <H3>{item.title}</H3>
          </CheckingRow>
        ))}
      </ItemsContainer>
    </>
  )

  return (
    <div ref={dropdownRef} onClick={() => setOpen(!open)}>
      <ButtonContainer open={open}>
        <Row nogutter justify="between">
          <H3 textAlign="left">{title}</H3>
          {open ? (
            <ArrowDropUpIcon color={theme.colors.greyDark} />
          ) : (
            <ArrowDropDownIcon color={theme.colors.greyDark} />
          )}
        </Row>
      </ButtonContainer>
      <Visible xs>
        <AnimatePresence initial={false} mode="wait" onExitComplete={() => null}>
          {open && (
            <Modal open={open} onClose={() => setOpen(false)} closeIcon>
              <DropdownContent />
            </Modal>
          )}
        </AnimatePresence>
      </Visible>
      <Hidden xs>
        <DropdownContentContainer open={open}>
          <DropdownContent />
        </DropdownContentContainer>
      </Hidden>
    </div>
  )
}

export default FilterDropdown
